import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, User, Code } from 'lucide-react';
import SEO from '../components/SEO';

export default function BlogAEMDispatcherCaching() {
  const canonical = 'https://divaisetech.com/blog/aem-dispatcher-caching-best-practices';
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPage',
    name: 'AEM Dispatcher Caching: Best Practices for Maximum Performance',
    url: canonical,
    headline: 'AEM Dispatcher Caching: Best Practices for Maximum Performance',
    description: 'Comprehensive guide to AEM Dispatcher caching configuration, invalidation strategies, performance optimization, and troubleshooting.',
    datePublished: '2025-01-01',
    author: {
      '@type': 'Organization',
      name: 'Divaise Technology Private Limited',
      url: 'https://divaisetech.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Divaise Technology Private Limited',
      url: 'https://divaisetech.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://divaisetech.com/logo.png',
      },
    },
    mainEntityOfPage: canonical,
  };

  return (
    <div>
      <SEO
        title="AEM Dispatcher Caching Best Practices | Performance Optimization Guide"
        description="Comprehensive guide to AEM Dispatcher caching configuration, invalidation strategies, performance optimization, and troubleshooting."
        keywords="AEM Dispatcher, Dispatcher caching, cache configuration, cache invalidation, AEM performance, Varnish"
        canonical={canonical}
        schema={articleSchema}
      />

      {/* Blog Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="inline-block bg-accent/20 text-accent px-4 py-2 rounded-full text-sm font-semibold mb-4">
            AEM Technical Guide
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">AEM Dispatcher Caching: Best Practices for Maximum Performance</h1>
          <p className="text-xl text-slate-200 mb-8">
            Deep dive into Dispatcher cache configuration, invalidation strategies, and optimization techniques to dramatically improve AEM site performance and reduce server load.
          </p>
          <div className="flex flex-wrap gap-6 text-slate-300">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>January 2025</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span>By AEM Engineers, Divaise</span>
            </div>
            <div>
              <span>16 min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <article className="py-16 px-6 bg-black">
        <div className="max-w-3xl mx-auto bg-black rounded-2xl p-8 md:p-10 border border-gray-800 [&_h2]:!text-white [&_h3]:!text-white [&_h4]:!text-white [&_p]:!text-slate-300 [&_li]:!text-slate-300 [&_td]:!text-slate-300 [&_th]:!text-white [&_summary]:!text-white [&_strong]:!text-white [&_pre]:!text-slate-200">
          {/* Introduction */}
          <section className="mb-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              AEM Dispatcher is the critical caching layer that stands between your publishes and the internet. Configured correctly, it can reduce publish load by 80-95%, improve page load times, and enable your AEM instance to handle 10-100× more traffic. Configured poorly, you'll see stale content, cache misses, and performance problems that are hard to debug.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              This guide covers Dispatcher architecture, caching strategies, cache invalidation patterns, and optimization techniques used in production AEM environments.
            </p>
          </section>

          {/* Architecture Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding AEM Dispatcher Architecture</h2>
            <p className="text-gray-700 mb-6">
              Dispatcher is typically an Apache or Nginx web tier that sits in front of your AEM publish instance(s). It intercepts HTTP requests and decides whether to serve from cache or forward to the backend.
            </p>
            <div className="bg-slate-950 border border-white/10 rounded-lg p-8 mb-6 font-mono text-sm overflow-x-auto text-slate-200">
              <pre>{`Client Browser
        ↓
   Internet / CDN
        ↓
  Dispatcher (Apache/Nginx)
    - Cache Layer
    - Request Routing
    - Cache Invalidation
        ↓
  AEM Publish Instance
    - Content Rendering
    - Form Processing
    - Search/API
        ↓
  Replication from Author`}</pre>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-950 rounded-lg p-6 border border-blue-500/30">
                <h3 className="font-bold text-gray-900 mb-4">Key Dispatcher Functions</h3>
                <ul className="space-y-3 text-gray-700">
                  <li>✓ Cache GET requests & static assets</li>
                  <li>✓ Forward POST requests to publish</li>
                  <li>✓ Invalidate cache on content changes</li>
                  <li>✓ Gzip compression</li>
                  <li>✓ Request filtering (security)</li>
                  <li>✓ Virtual host routing</li>
                </ul>
              </div>
              <div className="bg-slate-950 rounded-lg p-6 border border-green-500/30">
                <h3 className="font-bold text-gray-900 mb-4">Performance Impact</h3>
                <ul className="space-y-3 text-gray-700">
                  <li>📊 Cache hit ratio: 80-95%</li>
                  <li>⚡ Response time: 10-100ms (vs 500-2000ms)</li>
                  <li>📈 Server load: 80-90% reduction</li>
                  <li>💾 Bandwidth: 50-70% savings</li>
                  <li>🎯 Throughput: 10-100× increase</li>
                  <li>💰 Infrastructure cost reduction</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Cache Configuration */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Core Dispatcher Cache Configuration</h2>
            <p className="text-gray-700 mb-6">
              The dispatcher.conf file controls what gets cached, how long, and who can invalidate.
            </p>
            <div className="bg-gray-900 text-white rounded-lg p-6 font-mono text-sm mb-8 overflow-x-auto">
              <pre>{`# /etc/dispatcher.farm

# Basic cache settings
/cache {
  /docroot      "/var/cache/dispatcher"
  /statfileslevel "2"        # Directory structure depth
}

# Cache rules
/rules {
  /0034 {
    /glob "*"
    /type "allow"             # Cache all by default
  }
  
  # Don't cache sensitive pages
  /0005 {
    /glob "*.pdf"
    /type "deny"
  }
  
  # Don't cache query strings (unless handled)
  /0010 {
    /glob "*?*"
    /type "deny"
  }
}

# Cache invalidation settings
/invalidate {
  /0000 {
    /glob "*"
    /type "allow"
  }
}

# Flush settings (what triggers cache clear)
/allowedClients {
  /0000 {
    /glob "127.0.0.1"        # Publish server IP
    /type "allow"
  }
}

# Filter which HTTP methods get cached
/clientheaders {
  "Host"
  "User-Agent"
  "Cookie"    # Important for personalization
}

# Cache TTL (Time To Live)
/invalidateHandler "/dispatcher/invalidate.cache"
/statfiles [
  "/statfile.txt"        # Timestamp file for cache invalidation
  "/globalstatfile"
]`}</pre>
            </div>
            <div className="bg-slate-950 border border-blue-500/30 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-4">Key Configuration Parameters</h3>
              <div className="space-y-4">
                {[
                  {
                    param: 'docroot',
                    meaning: 'Physical directory where cached files are stored',
                    example: '/var/cache/dispatcher',
                  },
                  {
                    param: 'statfileslevel',
                    meaning: 'Directory depth for cached file structure (affects inode usage)',
                    example: '2 or 3 (prevents filesystem from having millions of files in one directory)',
                  },
                  {
                    param: 'rules',
                    meaning: 'Pattern matching rules to allow/deny caching',
                    example: 'Allow *.html, deny *.pdf, deny URLs with query parameters',
                  },
                  {
                    param: 'clientheaders',
                    meaning: 'HTTP headers to consider when caching (if different, separate cache)',
                    example: 'Host, User-Agent, Cookie headers create separate cache entries',
                  },
                ].map((item, idx) => (
                  <div key={idx} className="border-l-4 border-blue-400 pl-4">
                    <p className="font-mono font-bold text-gray-900">{item.param}</p>
                    <p className="text-gray-700 text-sm mb-1">{item.meaning}</p>
                    <p className="text-gray-600 text-xs">Example: {item.example}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Cache Invalidation */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Cache Invalidation Strategies</h2>
            <p className="text-gray-700 mb-6">
              Cache invalidation is the #1 source of stale content issues. You must have a solid strategy for clearing cache when content changes.
            </p>
            <div className="space-y-6 mb-8">
              {[
                {
                  strategy: 'Time-Based Expiration (TTL)',
                  pros: ['Simple to implement', 'Guarantees freshness', 'Passive invalidation'],
                  cons: ['Always expires (even if unchanged)', 'Cache miss rate higher'],
                  note: 'Typically 5-60 minutes. Good for high-traffic, low-update pages.',
                },
                {
                  strategy: 'Event-Based Invalidation',
                  pros: ['Immediate cache clear on publish', 'High cache efficiency', 'Zero stale content'],
                  cons: ['Requires custom logic', 'More complex setup'],
                  note: 'AEM sends HTTP INVALIDATE request on activation. Recommended approach.',
                },
                {
                  strategy: 'Wildcard Invalidation',
                  pros: ['Clears multiple pages at once', 'Handles hierarchy changes'],
                  cons: ['Can invalidate too much', 'Performance hit on large caches'],
                  note: 'Use patterns like /news/* or /products/* to invalidate sections.',
                },
                {
                  strategy: 'Statfile Invalidation',
                  pros: ['Atomic cache clearing', 'No manual intervention'],
                  cons: ['All cache expires simultaneously'],
                  note: 'Update timestamp of statfile triggers full cache clear.',
                },
              ].map((item, idx) => (
                <div key={idx} className="border-l-4 border-accent bg-slate-950 rounded-lg p-6">
                  <h3 className="font-bold text-gray-900 mb-3 text-lg">{item.strategy}</h3>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="font-semibold text-green-700 mb-2">✓ Pros:</p>
                      <ul className="text-gray-700 text-sm space-y-1">
                        {item.pros.map((pro, i) => <li key={i}>• {pro}</li>)}
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-orange-700 mb-2">⚠ Cons:</p>
                      <ul className="text-gray-700 text-sm space-y-1">
                        {item.cons.map((con, i) => <li key={i}>• {con}</li>)}
                      </ul>
                    </div>
                  </div>
                  <p className="text-gray-600 italic text-sm"><strong>Use Case:</strong> {item.note}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Best Practices */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Dispatcher Caching Best Practices</h2>
            <div className="space-y-6">
              {[
                {
                  title: '1. Cache Dynamic Content Carefully',
                  desc: 'Personalized content (user login status, preferences) should NOT be cached, or cache separately by user segment using headers.',
                },
                {
                  title: '2. Use Sensible Cache Invalidation Patterns',
                  desc: 'Don\'t invalidate the entire cache on every publish. Use specific patterns like /page/* to target sections needing updates.',
                },
                {
                  title: '3. Implement TTL for Safety',
                  desc: 'Even with event-based invalidation, set a maximum TTL to guarantee freshness. Typically 1-4 hours.',
                },
                {
                  title: '4. Monitor Cache Hit Ratio',
                  desc: 'Aim for 85-95% hit ratio. Lower ratios indicate configuration issues or excessive invalidation.',
                },
                {
                  title: '5. Use Gzip Compression',
                  desc: 'Cache gzipped versions of HTML/CSS/JS. Reduces bandwidth 70-80% with minimal CPU cost.',
                },
                {
                  title: '6. Implement Proper Logging',
                  desc: 'Log cache hits/misses/invalidations to analyze patterns and identify problems.',
                },
                {
                  title: '7. Separate Caching for Edit vs Publish',
                  desc: 'Author and publish have different caching needs. Author cache is typically very short (5 minutes).',
                },
                {
                  title: '8. Use Vanish for Advanced Caching',
                  desc: 'For large deployments, Varnish offers superior performance and flexibility compared to Apache mod_cache.',
                },
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-950 border border-white/10 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Cache Headers */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">HTTP Headers for Cache Control</h2>
            <p className="text-gray-700 mb-6">
              HTTP headers tell Dispatcher (and browsers) how to cache content.
            </p>
            <div className="bg-gray-900 text-white rounded-lg p-6 font-mono text-sm mb-8 overflow-x-auto">
              <pre>{`# AEM should emit these headers

# Standard cache control
Cache-Control: public, max-age=3600      # Cache 1 hour
Cache-Control: private, no-cache         # Don't cache
Cache-Control: must-revalidate           # Must check freshness

# Dispatcher specific
CQ-Action: DELETE                         # Invalidate this page
CQ-Action-Scope: wildcard                 # Pattern: /news/*

# Example response headers from AEM
HTTP/1.1 200 OK
Cache-Control: public, max-age=86400
Expires: Thu, 01 Jan 2025 12:00:00 GMT
ETag: "abc123"
Last-Modified: Wed, 31 Dec 2024 10:00:00 GMT
Vary: Accept-Encoding,Host`}</pre>
            </div>
            <div className="bg-slate-950 border border-blue-500/30 rounded-lg p-6">
              <h3 className="font-bold text-gray-900 mb-4">Key Cache Headers</h3>
              <div className="space-y-3 text-gray-700">
                <p><span className="font-mono font-bold">Cache-Control</span> — Primary directive for caching behavior (public/private, max-age)</p>
                <p><span className="font-mono font-bold">Expires</span> — Absolute expiration date/time</p>
                <p><span className="font-mono font-bold">ETag</span> — Hash of content for revalidation</p>
                <p><span className="font-mono font-bold">Last-Modified</span> — Last modification timestamp</p>
                <p><span className="font-mono font-bold">Vary</span> — Headers that create separate cache entries (Host, Accept-Encoding, etc.)</p>
              </div>
            </div>
          </section>

          {/* Troubleshooting */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Debugging Dispatcher Cache Issues</h2>
            <div className="space-y-6">
              {[
                {
                  problem: 'Stale content being served',
                  diagnosis: 'Check if cache invalidation is triggering. Look at Dispatcher logs. Verify AEM is sending CQ-Action headers.',
                  solution: 'Implement event-based invalidation, check replication agents, review dispatcher.conf rules.',
                },
                {
                  problem: 'Low cache hit ratio (< 70%)',
                  diagnosis: 'Too many misses indicate over-invalidation or poor rules. Check Dispatcher logs for patterns.',
                  solution: 'Review cache rules, reduce invalidation scope, implement TTL-based strategy.',
                },
                {
                  problem: 'Cache not invalidating at all',
                  diagnosis: 'Replication agent not working, network issue, or dispatcher.conf not allowing invalidation.',
                  solution: 'Test replication agent, check firewall, verify allowedClients IP, check Dispatcher logs.',
                },
                {
                  problem: 'Dispatcher running out of disk space',
                  diagnosis: 'Cache directory growing too large, typically from personalization or query string caching.',
                  solution: 'Implement cache size limits, exclude query strings, implement TTL-based expiration.',
                },
                {
                  problem: 'Personalized content showing to wrong users',
                  diagnosis: 'Personalization headers not being used to create separate cache entries (Vary header missing).',
                  solution: 'Add User-ID or similar to Vary header, cache by user segment, implement age-based invalidation.',
                },
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-950 border-l-4 border-orange-500 rounded-lg p-6">
                  <h3 className="font-bold text-gray-900 mb-2">🔴 {item.problem}</h3>
                  <p className="text-gray-700 mb-2"><span className="font-semibold">Diagnosis:</span> {item.diagnosis}</p>
                  <p className="text-gray-700"><span className="font-semibold">Solution:</span> {item.solution}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Advanced Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Advanced Dispatcher Patterns</h2>
            <div className="space-y-6">
              {[
                {
                  pattern: 'Load Balancing Across Multiple Dispatchers',
                  desc: 'For high-traffic sites, use multiple Dispatcher instances behind a load balancer, each with their own cache.',
                },
                {
                  pattern: 'Varnish as Advanced Cache Layer',
                  desc: 'Varnish provides better performance, advanced cache logic (ESI), and superior invalidation compared to Apache mod_cache.',
                },
                {
                  pattern: 'CDN Integration (CloudFlare, Akamai)',
                  desc: 'Layer a CDN on top of Dispatcher for geographic distribution and DDoS protection.',
                },
                {
                  pattern: 'Cache Warming',
                  desc: 'Pre-populate cache with critical pages on startup using scripts that crawl your site.',
                },
                {
                  pattern: 'Segment-Based Caching',
                  desc: 'Cache separate versions for user segments (logged-in vs anonymous, mobile vs desktop) using headers.',
                },
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-950 rounded-lg p-6 border border-blue-500/30">
                  <h3 className="font-bold text-gray-900 mb-2">{item.pattern}</h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Key Metrics */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Metrics to Monitor</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  metric: 'Cache Hit Ratio',
                  target: '85-95%',
                  how: 'Monitor logs: (cache hits) / (total requests)',
                },
                {
                  metric: 'Average Response Time',
                  target: '< 100ms',
                  how: 'Check Dispatcher & CDN timings',
                },
                {
                  metric: 'Cache Size',
                  target: '< 500GB',
                  how: 'Monitor disk space, implement expiration',
                },
                {
                  metric: 'Invalidation Frequency',
                  target: 'Varies by site',
                  how: 'Count INVALIDATE requests per day',
                },
                {
                  metric: 'Bandwidth Savings',
                  target: '60-80% reduction',
                  how: 'Compare traffic with/without cache',
                },
                {
                  metric: 'Publish Server Load',
                  target: '10-20% of traffic',
                  how: 'Monitor CPU, memory usage',
                },
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-950 rounded-lg p-6 border border-white/10">
                  <h3 className="font-bold text-gray-900 mb-2">{item.metric}</h3>
                  <p className="text-accent font-semibold mb-2">Target: {item.target}</p>
                  <p className="text-gray-700 text-sm">{item.how}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-accent to-accent/80 text-black rounded-lg p-10 text-center">
            <h2 className="text-2xl font-bold mb-4">Need Help Optimizing Your AEM Dispatcher?</h2>
            <p className="text-lg mb-6 text-black/80">
              Our AEM performance experts can audit your current setup and implement optimization strategies.
            </p>
            <Link
              to="/free-aem-consultation"
              className="inline-flex items-center bg-black text-white px-6 py-3 rounded-lg font-semibold hover:bg-black/90 transition-all"
            >
              Schedule Performance Consultation <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </section>
        </div>
      </article>

      {/* Related Articles */}
      <section className="py-16 px-6 bg-black">
        <div className="max-w-3xl mx-auto">
          <h3 className="text-2xl font-bold text-white mb-8">Related Articles</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: 'AEM 6.5 vs AEM Cloud Comparison',
                link: '/blog/aem-6-5-vs-aem-cloud',
              },
              {
                title: 'Enterprise Cloud Migration Strategy',
                link: '/blog/enterprise-cloud-migration-strategy',
              },
            ].map((article, idx) => (
              <Link
                key={idx}
                to={article.link}
                className="group bg-gray-900 border border-gray-700 rounded-lg p-6 hover:shadow-lg transition-all"
              >
                <h4 className="font-bold text-white group-hover:text-accent transition-colors">{article.title}</h4>
                <p className="text-accent text-sm mt-3 flex items-center gap-1">
                  Read More <ArrowRight className="w-3 h-3" />
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
